import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Pagination, Spin, TablePaginationConfig } from "antd";
import BasicButton from "@components/core/buttons/BasicButton";
import BasicTable from "@components/core/tables/BasicTable";
import { columns } from "@views/core/extranet/menu/offices/group/columns/GroupColumns";
import BasicTabs from "@components/core/tabs/BasicTabs";
import {
  Group,
  GroupMembership,
  PageGroupMembership,
} from "@type/core/dashboard/dashboard.types";
import { FilterValue, SorterResult } from "antd/es/table/interface";

interface GroupMemberShipTableProps {
  isLoading: boolean;
  groupsToDisplay: Group[];
  handleSearch: (groupId?: string) => void;
  setIsAddGroupMembership: Dispatch<SetStateAction<boolean>>;
  setIsDeleteGroupMembershipOpen: Dispatch<SetStateAction<boolean>>;
  setIsDisableGroupMembershipOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedGroupMembership: Dispatch<
    SetStateAction<GroupMembership | undefined>
  >;
  groupMemberships: PageGroupMembership | undefined;
  setSorter: Dispatch<SetStateAction<{ type: string; order: number }>>;
  setPage: Dispatch<SetStateAction<number>>;
}

const GroupMemberShipTable: FunctionComponent<GroupMemberShipTableProps> = ({
  isLoading,
  groupsToDisplay,
  handleSearch,
  setIsAddGroupMembership,
  setIsDeleteGroupMembershipOpen,
  setIsDisableGroupMembershipOpen,
  setSelectedGroupMembership,
  groupMemberships,
  setSorter,
  setPage,
}) => {
  const { t } = useTranslation();

  const membershipRetrieving = false;
  const sirenRetrieving = false;
  const siren = ["000 000 000 0000"];
  const PAGE_SIZE = 10;

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<GroupMembership> | SorterResult<GroupMembership>[],
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSorter({
      type: singleSorter.field as string,
      order: singleSorter.order === "ascend" ? 1 : 0,
    });
  };

  return (
    <BasicTabs
      onTabChange={handleSearch}
      tabs={groupsToDisplay?.map((group) => {
        return {
          title: group.name,
          content: (
            <>
              {membershipRetrieving ? (
                <div className="h-100 flex-grow-1 d-flex align-items-center justify-content-center">
                  <Spin />
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h3>{t("offices.group.subtitle")}</h3>
                    {sirenRetrieving ? (
                      <div className="h-100 flex-grow-1 d-flex align-items-center justify-content-center">
                        <Spin />
                      </div>
                    ) : siren.length > 0 ? (
                      <div className="d-flex align-items-center justify-content-center mb-24">
                        <BasicButton
                          text={t("offices.group.addGroupMembership.title")}
                          className="mw-340"
                          onClick={() => setIsAddGroupMembership(true)}
                        />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center mb-24">
                        {t("offices.group.noSiren")}
                      </div>
                    )}
                  </div>
                </>
              )}
              {groupMemberships?.content &&
                groupMemberships.content.length !== 0 && (
                  <div className="min-height-table">
                    <BasicTable
                      isLoading={isLoading}
                      onChange={handleTableChange}
                      columns={columns(
                        setIsDeleteGroupMembershipOpen,
                        setIsDisableGroupMembershipOpen,
                        setSelectedGroupMembership,
                      )}
                      dataSource={groupMemberships.content}
                      rowKey={"id"}
                    />
                    <div className="pagination-center mt-15">
                      <Pagination
                        className="justify-content-center"
                        showSizeChanger={false}
                        pageSize={PAGE_SIZE}
                        total={groupMemberships.max * PAGE_SIZE}
                        showLessItems
                        onChange={(page: number) => {
                          setPage(page);
                        }}
                      />
                    </div>
                  </div>
                )}
            </>
          ),
          officeId: group.id,
        };
      })}
    />
  );
};

export default GroupMemberShipTable;
